import React, { useState } from "react";
import apiEndpoints from "@store/apiEndpoints";
import errorFunctions from "@store/errorFunctions";
import referenceLibrary from "@store/referenceLibrary";
import { useToast } from "@components/Toast/ToastContext";
import ConfirmModal from "../ConfirmModal";
import {
  FormSection,
  FieldGrid,
  TextAreaField,
  CheckboxGrid,
  ToggleRow,
  Dropzone,
  employeeLabel,
} from "../FormBits";
import { SERVICE_TAB } from "../../mock/caseDetail";

// Every SERVICE_TAB.serviceTypes option but DOA, as its `ticket_type` value.
// Only warranty / out_of_warranty are known backend values (the old
// TicketProcess.js sends nothing else); the other three are assumed and still
// need confirming with the backend.
const TICKET_TYPES = {
  "In-Warranty": "warranty",
  "Out-of-Warranty": "out_of_warranty",
  "Accessories Only": "accessories_only",
  Quotation: "quotation",
  Others: "others",
};

// SERVICE_TAB.outcomes as `type_of_service` values. The first four are the old
// "Type of service" options (referenceLibrary.warrantyTypes); Quotation and
// Maintenance Clean are assumed and still need confirming with the backend.
const OUTCOMES = {
  Repair: "service",
  Credit: "credit_claim",
  "Parts Replace Only": "spare_parts_only",
  Replace: "replacement",
  Quotation: "quotation",
  "Maintenance Clean": "maintenance_clean",
};

// The type_of_service values TicketProcess.js's getWarrantyTypeOptions() offers
// for this ticket.
const allowedServiceValues = function (ticketType, brandTag) {
  let types = referenceLibrary.warrantyTypes;
  if (ticketType === "out_of_warranty") types = referenceLibrary.ooWarrantyTypes;
  else if (brandTag === "steelseries") types = referenceLibrary.ooWarrantyTypesSteelseries;
  return types.map(function (type) {
    return type.value;
  });
};

const KNOWN_SERVICE_VALUES = referenceLibrary.warrantyTypes.map(function (type) {
  return type.value;
});

/*
Service Type is one choice - a DOA ticket shows only DOA ticked, any other
ticket shows its `ticket_type`. Every change asks for confirmation first, then
reloads, as the "Ticket type" select in
Warranties/SingleWarranty/TicketProcess/TicketProcess.js does:
- DOA PATCHes `dead_on_arrival` through ticket/{id}/ (the page's Save
  endpoint, sent straight away rather than through the draft).
- Every other option PATCHes ticket/{id}/change-type/ with the new ticket_type
  and the current type_of_service, as TicketProcess.js does. On a DOA ticket
  DOA is cleared first, so the picked type is what shows.
The current choice can't be unticked into "none". Out-of-Warranty is locked
for SteelSeries, as the old select hides it.

Outcome is the ticket's `type_of_service`, one choice, with the same
confirm-then-reload flow as TicketProcess.js's "Type of service" select: it
PATCHes ticket/{id}/change-type/ with the current ticket_type and the new
type_of_service. Outcomes the old select doesn't offer for this ticket type
(e.g. Replace on an out-of-warranty ticket) are locked; the two assumed ones
aren't, since the old rules don't cover them. Its Details text has no field.

Fault Description, Repairer and the radio Result read off real
ticket fields. Backend confirmed (2026-09-25) `assigned_to` is the assigned
Employee's id (int), null when unassigned - not a name, so it's shown via
employeeLabel() rather than guessed at as a string/object. In Status Updates,
"Update Solvup" reads as ticked once the ticket has a `solvup_id` (set from the
Product tab's Solvup Case #). The other toggles and Assigned for Approval have
no backing field at all yet (no notification/approval-accepted flags, no
structured approver) - left unselected/blank.
*/
const ServiceTab = function (props) {
  const { protectedFetch, reloadTicket } = props;
  const { showToast } = useToast();
  const ticket = props.ticket || {};
  const canEdit = !!props.canEdit;

  const userDevice = ticket.user_device || {};
  const device = userDevice.device || {};
  const brandTag = (device.brand && device.brand.tag) || "";

  // The change waiting on confirmation: { field: "type" | "outcome", option }.
  const [pending, setPending] = useState(null);
  const [saving, setSaving] = useState(false);

  const isDoa = ticket.dead_on_arrival === true;
  const currentTicketType = Object.keys(TICKET_TYPES).find(function (option) {
    return TICKET_TYPES[option] === ticket.ticket_type;
  });
  const currentServiceType = isDoa ? "DOA" : currentTicketType;
  const serviceTypeSelected = currentServiceType ? [currentServiceType] : [];

  const lockedServiceTypes = brandTag === "steelseries" ? ["Out-of-Warranty"] : [];

  const toggleServiceType = function (option, checked) {
    if (!checked || option === currentServiceType) return;
    if (option !== "DOA" && !TICKET_TYPES[option]) return;
    setPending({ field: "type", option: option });
  };

  const currentOutcome = Object.keys(OUTCOMES).find(function (option) {
    return OUTCOMES[option] === ticket.type_of_service;
  });
  const allowedOutcomeValues = allowedServiceValues(ticket.ticket_type, brandTag);
  const lockedOutcomes = Object.keys(OUTCOMES).filter(function (option) {
    const value = OUTCOMES[option];
    return KNOWN_SERVICE_VALUES.includes(value) && !allowedOutcomeValues.includes(value);
  });

  const toggleOutcome = function (option, checked) {
    if (!checked || option === currentOutcome || !OUTCOMES[option]) return;
    setPending({ field: "outcome", option: option });
  };

  const patchTicket = function (url, payload) {
    return protectedFetch(url, "PATCH", payload, errorFunctions.checkResponseThenJson);
  };

  const saveServiceType = function (option) {
    if (option === "DOA") {
      return patchTicket(apiEndpoints.ticket.update(ticket.id), { dead_on_arrival: true });
    }
    const clearDoa = isDoa
      ? patchTicket(apiEndpoints.ticket.update(ticket.id), { dead_on_arrival: false })
      : Promise.resolve();
    return clearDoa.then(function () {
      if (TICKET_TYPES[option] === ticket.ticket_type) return null;
      return patchTicket(apiEndpoints.ticket.changeType(ticket.id), {
        ticket_type: TICKET_TYPES[option],
        type_of_service: ticket.type_of_service || "",
      });
    });
  };

  const saveOutcome = function (option) {
    return patchTicket(apiEndpoints.ticket.changeType(ticket.id), {
      ticket_type: ticket.ticket_type || "warranty",
      type_of_service: OUTCOMES[option],
    });
  };

  const pendingCopy = pending
    ? pending.field === "outcome"
      ? { title: "Ticket outcome", noun: "outcome" }
      : { title: "Ticket service type", noun: "service type" }
    : null;

  const confirmPending = function () {
    if (!pending || !ticket.id) return;
    const noun = pendingCopy.noun;
    setSaving(true);
    (pending.field === "outcome" ? saveOutcome : saveServiceType)(pending.option)
      .then(function () {
        showToast(noun.charAt(0).toUpperCase() + noun.slice(1) + " updated", "success");
        setPending(null);
      })
      .catch(function (err) {
        console.log("fail", err);
        showToast("Failed to update " + noun, "error");
      })
      .finally(function () {
        setSaving(false);
        // Also after a failure: clearing DOA may have gone through.
        if (reloadTicket) reloadTicket();
      });
  };

  const repairer = [
    { label: "ASC", placeholder: "Authorised service centre", value: ticket.service_center_name },
    {
      label: "Repairer",
      placeholder: "Pegatron / Subemo technician name",
      value: employeeLabel(ticket.assigned_to),
    },
  ];

  const statusChecked = {
    "Update Solvup": !!(ticket.solvup_id && String(ticket.solvup_id).trim()),
  };

  const faultFound = ticket.fault_replicated_in_store === true;
  const faultNotFound = ticket.fault_replicated_in_store === false;

  return (
    <div className="tn-tabpanel">
      <FormSection title="Service Type">
        <CheckboxGrid
          options={SERVICE_TAB.serviceTypes}
          selected={serviceTypeSelected}
          onToggle={toggleServiceType}
          disabled={!canEdit || saving}
          disabledOptions={lockedServiceTypes}
        />
      </FormSection>

      <FormSection title="Fault Description" note="Synced from Product tab">
        <CheckboxGrid options={SERVICE_TAB.faults} selected={ticket.fault_types} />
        <TextAreaField
          label="Details"
          placeholder="Fault details carried over from the Product tab…"
          value={ticket.replication_steps || ticket.other_issue}
        />
      </FormSection>

      <FormSection title="Repairer">
        <FieldGrid fields={repairer} cols={2} />
      </FormSection>

      <FormSection title="Result" note="Full write-up in the Report tab">
        <div className="tn-radiorow">
          <label className="tn-check">
            <input type="radio" name="tn-svc-result" defaultChecked={faultFound} disabled />
            <span>Fault Found</span>
          </label>
          <label className="tn-check">
            <input type="radio" name="tn-svc-result" defaultChecked={faultNotFound} disabled />
            <span>No Fault Found</span>
          </label>
        </div>
        <TextAreaField
          label="Brief Result Description"
          placeholder="No single field for this - see the diagnostic/repair summaries in the Report tab."
        />
      </FormSection>

      <FormSection title="Outcome">
        <CheckboxGrid
          options={SERVICE_TAB.outcomes}
          selected={currentOutcome ? [currentOutcome] : []}
          onToggle={toggleOutcome}
          disabled={!canEdit || saving}
          disabledOptions={lockedOutcomes}
        />
        <TextAreaField
          label="Details"
          placeholder="e.g. Replaced whole unit, repaired with faulty motor replaced, consumable replaced."
        />
      </FormSection>

      <FormSection title="Status Updates">
        <div className="tn-togglestack">
          {SERVICE_TAB.statusToggles.map(function (label) {
            return <ToggleRow key={label} label={label} checked={statusChecked[label]} />;
          })}
        </div>
      </FormSection>

      <FormSection title="Assigned for Approval">
        <CheckboxGrid options={SERVICE_TAB.approvers} />
        <TextAreaField label="Reason" placeholder="Reason for approval routing" />
        <TextAreaField label="Notes / Remark" placeholder="Internal notes" />
      </FormSection>

      <FormSection title="Attachments">
        <Dropzone
          title="ASC Repair Report"
          hint="Upload repair report or service-related attachments"
        />
      </FormSection>

      <ConfirmModal
        open={!!pending}
        title={pendingCopy && pendingCopy.title}
        message={pending ? "Change " + pendingCopy.noun + " to " + pending.option + "?" : null}
        busy={saving}
        onConfirm={confirmPending}
        onCancel={function () {
          if (!saving) setPending(null);
        }}
      />
    </div>
  );
};

export default ServiceTab;
